import { forwardRef } from 'react';

import clsx from 'clsx';
import { isSameDay } from 'date-fns';

import {
  datePickerPresetHeight,
  presetDivider,
  presetDividerLine,
  presetHeader,
  presetItem,
  presetList,
  presetPanel,
  presetYearText,
} from './DatePicker.css';
import { DatePickerPresetProps } from './types';
import { Colors } from '../../tokens/colors';
import { IconButton } from '../Button';
import { IconCheckLine, IconKeyboardArrowLeftLine, IconKeyboardArrowRightLine } from '../Icon';
import { Scrollbar } from '../Scrollbar';

export const DatePickerPreset = forwardRef<HTMLDivElement, DatePickerPresetProps>(
  ({ presetGroups, selectedRange, onSelect, year, onYearChange }, ref) => {
    const currentYear = new Date().getFullYear();
    const isCurrentYear = year === currentYear;

    const handlePrevYear = () => {
      onYearChange(year - 1);
    };

    const handleNextYear = () => {
      onYearChange(year + 1);
    };

    return (
      <div ref={ref} className={presetPanel}>
        <div className={presetHeader}>
          <span className={presetYearText}>{year}년</span>
          <IconButton
            buttonType="ghost"
            buttonSize="s"
            icon={<IconKeyboardArrowLeftLine width={18} height={18} fill={Colors.gray900} />}
            tooltipMessage="이전 년도"
            onClick={handlePrevYear}
          />
          <IconButton
            buttonType="ghost"
            buttonSize="s"
            icon={<IconKeyboardArrowRightLine width={18} height={18} fill={Colors.gray900} />}
            tooltipMessage="다음 년도"
            onClick={handleNextYear}
          />
        </div>
        <Scrollbar
          noScrollX
          style={{ height: `${datePickerPresetHeight}px` }}
          scrollbarStyle={{ paddingRight: 0 }}
        >
          <div className={clsx(presetList)}>
            {presetGroups.map((group, groupIndex) => (
              <div key={groupIndex}>
                {group.items.map((preset) => {
                  const presetValue = preset.getValue();
                  const isSelected =
                    presetValue.from &&
                    presetValue.to &&
                    selectedRange?.from &&
                    selectedRange?.to &&
                    isSameDay(selectedRange.from, presetValue.from) &&
                    isSameDay(selectedRange.to, presetValue.to);
                  // Disable relative presets (오늘, 어제, etc.) when not current year
                  const isRelativePreset = [
                    'today',
                    'yesterday',
                    'thisWeek',
                    'lastWeek',
                    'thisMonth',
                    'lastMonth',
                    'thisYear',
                  ].includes(preset.key);
                  const isDisabled = !isCurrentYear && isRelativePreset;

                  return (
                    <button
                      key={preset.key}
                      className={presetItem({ selected: isSelected, disabled: isDisabled })}
                      onClick={() => !isDisabled && onSelect(preset)}
                      disabled={isDisabled}
                    >
                      <span style={{ flex: 1 }}>{preset.label}</span>
                      {isSelected && <IconCheckLine width={20} height={20} fill={Colors.gray900} />}
                    </button>
                  );
                })}
                {group.divider && groupIndex < presetGroups.length - 1 && (
                  <div className={presetDivider}>
                    <div className={presetDividerLine} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </Scrollbar>
      </div>
    );
  },
);

DatePickerPreset.displayName = 'DatePickerPreset';
